import fixWebmDuration from 'fix-webm-duration'
import {
  CAPTURE_REVIEW_DIALOG_DISPLAY_REQUESTED,
  PAGE_FADER_HIDE_REQUESTED,
  PAGE_FADER_SHOW_REQUESTED,
} from '~/types/bus-event-names'
import {useUserStore} from '~/stores/user'

import {CognitoIdentityClient} from '@aws-sdk/client-cognito-identity'
import {fromCognitoIdentityPool} from '@aws-sdk/credential-provider-cognito-identity'
import {PutObjectCommand, type PutObjectCommandInput, S3Client} from '@aws-sdk/client-s3'
import {useMediaStore} from '~/stores/media'
import {useMediaFrameElements} from '~/composables/studio/useMediaFrameElements'
import {useElementOffset} from '~/composables/dom/useElementOffset'
import {useUserVideoOverlay} from '~/composables/studio/useUserVideoOverlay'
import type {CapturePageData} from '~/types/media/CapturePageData'
import {useDirectoryStore} from '~/stores/directory'
import type {DirectoryData} from '~/types/DirectoryData'
import type {LocationQueryRaw} from 'vue-router'
import {doTickComposition} from '~/composables/studio/useMediaComposition'

const {
  getFrameDocument,
  getStageCanvas,
  getCompositeCanvasHeight,
} = useMediaFrameElements()

const {
  initialize: initVideoOverlay,
  hasVideo,
  webcamIsActive,
  webcamIsMaximized,
  stopUserStream,
} = useUserVideoOverlay()

const maxRecordMinutes = 5
const maxRecordSeconds = maxRecordMinutes * 60
const maxRecordWarningSeconds = 15

let canvasWidthWhenRecordingStarted: number
let recordingStartDate: Date
let recordInterval: NodeJS.Timeout

const recordSeconds = ref(0)
const recordDurationMinutes = computed(() => {
  return Math.floor(recordSeconds.value / 60)
})
const recordDurationSeconds = computed(() => {
  return Math.floor(recordSeconds.value - 60 * recordDurationMinutes.value)
})
const humanRecordMinutes = computed(() => {
  const v = recordDurationMinutes.value
  return v >= 10 ? v : '0' + v
})
const humanRecordSeconds = computed(() => {
  const v = recordDurationSeconds.value
  return v >= 10 ? v : '0' + v
})
const humanDuration = computed(() => {
  return humanRecordMinutes.value + ':' + humanRecordSeconds.value
})

const recordStep = ref<RecordStep>('closed')
const countdownDisplay = ref<number>(0)
let countdownInterval: NodeJS.Timeout
const endApproaching = ref(false)

let frameWrapper: HTMLDivElement
let mediaFrame: HTMLIFrameElement
let initializeOptions: MediaVideoCaptureOptions | null

let frameDocument: Document
let stageCanvas: HTMLCanvasElement

let mousePosition: { x: number, y: number } | null = null
let compositeCanvas: HTMLCanvasElement
let cursorImage: HTMLImageElement
let stampImage: HTMLImageElement

let chosenMimeType: string
let recorderChunks: Blob[]
let recorder: MediaRecorder
let recorderParams: MediaRecorderOptions
let mediaStage: createjs.Stage
let tickComposition: Function
let blob: Blob
let blobExtension: string

const videoUploadStatus = ref<'idle' | 'uploading' | 'uploaded'>('idle')
const videoIsUploaded = computed(() => {
  return videoUploadStatus.value === 'uploaded'
})
let lastUploadedCaptureData = ref<CapturePageData | null>(null)
const awsRegion = 'eu-west-3'
const awsIdentityPoolId = 'eu-west-3:9e95344f-7dac-4ee5-b9f0-726ef5b785c0'
const awsBucket = 'edumedia-capture-raw'
const s3: S3Client = new S3Client({
  region: awsRegion,
  credentials: fromCognitoIdentityPool({
    client: new CognitoIdentityClient({region: awsRegion}),
    identityPoolId: awsIdentityPoolId,
  }),
})

type RecordStep = 'closed' | 'idle' | 'countdown' | 'recording'

type MediaVideoCaptureOptions = {
  directoryOwnerStringRepresentation?: string
}

async function initialize(_frameWrapper: HTMLDivElement, _mediaFrame: HTMLIFrameElement, _options?: MediaVideoCaptureOptions): Promise<boolean> {
  frameWrapper = _frameWrapper
  mediaFrame = _mediaFrame
  initializeOptions = _options || null

  const doc = getFrameDocument(_mediaFrame)

  if (!doc) {
    throw new Error('Could not find frame document')
  }

  const canvas = getStageCanvas(_mediaFrame)

  if (!canvas) {
    throw new Error('Could not find stage canvas')
  }

  initRecorderParams()
  await initVideoOverlay(_mediaFrame)

  frameDocument = doc
  stageCanvas = canvas

  initOverlayImages()

  if (!compositeCanvas) {
    compositeCanvas = document.createElement('canvas')
  }

  frameDocument.defaultView?.removeEventListener('mousemove', onFrameMouseMove)
  frameDocument.defaultView?.addEventListener('mousemove', onFrameMouseMove)

  frameDocument.defaultView?.removeEventListener('resize', onFrameResize)
  frameDocument.defaultView?.addEventListener('resize', onFrameResize)

  recordStep.value = 'idle'

  return true
}

function onFrameMouseMove(e: MouseEvent) {
  const o = useElementOffset(stageCanvas)
  mousePosition = {
    x: e.clientX - o.left,
    y: e.clientY - o.top,
  }
}

function onFrameResize() {
  if (recordStep.value === 'recording') {
    console.warn('Resized while recording: had to stop')
    stopRecording()
  }
}

function initRecorderParams(): void {
  const wantedMimeTypes = [
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=vp8',
    'video/webm',
    // Safari
    'video/mp4;codecs=avc1',
    'video/mp4;codecs=AVC1',
    'video/mp4;codecs=mp4a',
    'video/mp4;codecs=MP4A',
    'video/mp4',
  ]

  for (let i = 0; i < wantedMimeTypes.length; i++) {
    const type = wantedMimeTypes[i]
    try {
      if (MediaRecorder.isTypeSupported(type)) {
        chosenMimeType = type
        recorderParams = {mimeType: type}
        break
      }
    } catch (e) {
    }
  }

  if (!recorderParams) {
    throw new Error('Could not pick compatible mime type')
  }
}

function initOverlayImages() {
  cursorImage = new Image()
  cursorImage.src = '/assets/images/capture/cursor.png'

  stampImage = new Image()
  stampImage.src = '/assets/images/capture/logo-stamp.png'
}

const canBeStopped = computed(() => {
  return ['countdown', 'recording'].indexOf(recordStep.value) > -1
})

const isClosed = computed(() => {
  return recordStep.value === 'closed'
})

const isCountingDown = computed(() => {
  return recordStep.value === 'countdown'
})

const isRecording = computed(() => {
  return recordStep.value === 'recording'
})

function startCountdown() {
  recordStep.value = 'countdown'
}

function stopRecording() {
  if (recordStep.value === 'recording') {
    stopRecordSequence()
  }

  recordStep.value = 'idle'
}

watch(recordStep, (newStep: string, previousStep: string) => {
  if (newStep === 'countdown') {
    countdownDisplay.value = 3
    countdownInterval = setInterval(decrementCountdownDisplay, 1000)
    videoUploadStatus.value = 'idle'
  } else if (previousStep === 'countdown') {
    clearInterval(countdownInterval)
  }

  if (newStep === 'recording') {
    afterCountdown()
  }

  if (newStep === 'closed') {
    useNuxtApp().$mitt.emit(PAGE_FADER_HIDE_REQUESTED)
    webcamIsActive.value = false
    webcamIsMaximized.value = false
    stopUserStream()
  } else {
    useNuxtApp().$mitt.emit(PAGE_FADER_SHOW_REQUESTED)
  }
})

function decrementCountdownDisplay() {
  countdownDisplay.value = countdownDisplay.value - 1

  if (countdownDisplay.value === 0) {
    clearInterval(countdownInterval)
    recordStep.value = 'recording'
  }

}

function afterCountdown() {
  canvasWidthWhenRecordingStarted = stageCanvas.width
  recordingStartDate = new Date()

  startRecording()
}

function startRecording() {
  prepareComposition()
  doComposition()

  initializeMediaRecorder()

  recordInterval = setInterval(onRecordInterval, 100)
  onRecordInterval()
}

function prepareComposition() {
  compositeCanvas.width = stageCanvas.width
  compositeCanvas.height = getCompositeCanvasHeight(stageCanvas)
}

function doComposition() {
  doTickComposition(
      mediaFrame,
      compositeCanvas,
      {
        mousePosition,
        cursorImage,
        recordingStartDate,
        initializeOptions,
        stampImage,
      },
  )
}

function initializeMediaRecorder() {
  const finalStream = new MediaStream()

  const {userMediaStream} = useUserVideoOverlay()

  if (userMediaStream) {
    userMediaStream.getAudioTracks().forEach(function (track: MediaStreamTrack) {
      finalStream.addTrack(track)
    })
  }

  const canvasStream = compositeCanvas.captureStream()
  canvasStream.getVideoTracks().forEach(function (track: MediaStreamTrack) {
    finalStream.addTrack(track)
  })

  recorderChunks = []
  recorder = new MediaRecorder(finalStream, recorderParams)
  recorder.ondataavailable = (e: BlobEvent) => {
    blobExtension = e.data.type.split('/').pop() as string
    recorderChunks.push(e.data)
  }
  recorder.onstop = () => {
    blob = new Blob(recorderChunks)
    recorderChunks = []

    if (blob.size === 0) {
      console.warn('Empty blob, capture failed :(')
      return
    }

    if (chosenMimeType.indexOf('webm') > -1) {
      fixWebmDuration(blob, recordSeconds.value * 1000, function (fixedBlob: Blob) {
        onBlobReady(fixedBlob)
      })
    } else {
      onBlobReady(blob)
    }
  }
  recorder.start()

  // @ts-ignore
  mediaStage = mediaFrame.contentWindow?.stage

  if (useRuntimeConfig().public.fakeFrameCapture) {
    return
  }

  tickComposition = mediaStage.on('tick', doComposition)
}

function onRecordInterval() {
  recordSeconds.value = ((new Date()).getTime() - recordingStartDate.getTime()) / 1000

  // Show end approaching alert

  const warnStart = maxRecordSeconds - maxRecordWarningSeconds
  const warnStartSeconds = warnStart % 60
  const warnStartMinutes = (warnStart - warnStartSeconds) / 60

  endApproaching.value = recordDurationMinutes.value >= warnStartMinutes && recordDurationSeconds.value >= warnStartSeconds

  // Adding an extra second for the video to really be the intended duration
  const warnEndSeconds = (maxRecordSeconds + 1) % 60
  const warnEndMinutes = (maxRecordSeconds + 1 - warnEndSeconds) / 60

  if (recordDurationMinutes.value === warnEndMinutes && recordDurationSeconds.value === warnEndSeconds) {
    stopRecordSequence()
  }
}

function stopRecordSequence() {
  clearInterval(recordInterval)
  if (!useRuntimeConfig().public.fakeFrameCapture) {
    mediaStage.off('tick', tickComposition)
  }
  recorder.stop()
}

function onBlobReady(b: Blob) {
  blob = b
  let videoURL

  try {
    videoURL = URL.createObjectURL(blob)
  } catch (e) {
    console.error(e)
    return
  }

  videoUploadStatus.value = 'idle'

  useNuxtApp().$mitt.emit(CAPTURE_REVIEW_DIALOG_DISPLAY_REQUESTED, {
    videoURL,
    chosenMimeType,
    width: compositeCanvas.width,
    height: compositeCanvas.height,
  })
}

const captureTransitionDurationClass = 'duration-500'

async function uploadVideo() {
  if (videoUploadStatus.value !== 'idle') {
    return
  }

  const mediaStore = useMediaStore()

  if (!mediaStore.currentMedia) {
    console.error('No media selected')
    return
  }

  videoUploadStatus.value = 'uploading'

  let fd = new FormData()
  fd.append('duration', recordSeconds.value + '')
  fd.append('width', compositeCanvas.width + '')
  fd.append('height', compositeCanvas.height + '')


  const directoryCode = useDirectoryStore().originDirectoryCode
  if (directoryCode) {
    fd.append('dir', directoryCode)
  }

  const {
    data: initData,
    error: initError,
  } = await useApiFetch<CapturePageData>(`/capture/initialize/${mediaStore.currentMedia.id}`, {
    method: 'post',
    body: fd,
  })

  if (initError.value || !initData.value) {
    console.error('Could not initialize capture instance')
    videoUploadStatus.value = 'idle'
    return
  }

  lastUploadedCaptureData.value = initData.value
  let captureCode: string = initData.value.code
  const filename = `${captureCode}.${blobExtension}`

  const uploadParams: PutObjectCommandInput = {
    Bucket: awsBucket,
    Key: filename,
    Body: new File([blob], filename),
    ACL: 'public-read',
  }

  try {
    await s3.send(new PutObjectCommand(uploadParams))
  } catch (err) {
    console.error(err)
    videoUploadStatus.value = 'idle'
    return
  }

  fd = new FormData()
  fd.append('Bucket', awsBucket)
  fd.append('Key', filename)
  fd.append('Location', `https://${uploadParams.Bucket}.s3.${awsRegion}.amazonaws.com/${uploadParams.Key}`)
  if (directoryCode) {
    fd.append('dir', directoryCode)
  }
  const {
    data: postUploadData,
    error: postUploadError,
  } = await useApiFetch<CapturePageData>(`/capture/${captureCode}/post-upload`, {method: 'post', body: fd})

  if (postUploadError.value || !postUploadData.value) {
    console.error('Post upload hook failed')
    videoUploadStatus.value = 'idle'
    return
  }

  videoUploadStatus.value = 'uploaded'
  lastUploadedCaptureData.value = postUploadData.value
}

async function submitVideo(name: string, studentName: string, directoriesChecked: DirectoryData[] | null) {
  if (!lastUploadedCaptureData.value) {
    console.error('Missing lastUploadedCaptureData')
    return
  }

  const fd = new FormData()
  fd.append('name', name)

  const directoryCode = useDirectoryStore().originDirectoryCode
  if (directoryCode) {
    fd.append('dir', directoryCode)
    fd.append('studentName', studentName)
  }
  if (directoriesChecked) {
    fd.append('dirPositionByCode', JSON.stringify(directoriesChecked.map(directory => {
      return {'code': directory.code, 'position': directory.favorites.length}
    })))
  }
  const {error} = await useApiFetch(`/capture/${lastUploadedCaptureData.value.code}/submit`, {method: 'post', body: fd})

  if (error.value) {
    console.error('Capture submit failed')
  }

  if (useUserStore().isAuthenticated) {
    await useSession().refreshSession()
    return navigateTo(useNuxtApp().$localePath({name: 'me-captures'}))
  }

  const query: LocationQueryRaw = {}
  if (directoryCode) {
    query['dir'] = directoryCode
  }

  return navigateTo(useNuxtApp().$localePath({
    name: 'capture-code',
    params: {
      code: lastUploadedCaptureData.value.code,
    },
    query,
  }))
}

export const useMediaVideoCapture = () => {
  return {
    captureTransitionDurationClass,

    recordStep,
    isClosed,
    isCountingDown,
    isRecording,
    canBeStopped,

    initialize,
    startCountdown,
    stopRecording,
    countdownDisplay,
    maxRecordMinutes,

    // hasAudio,
    hasVideo,
    webcamIsActive,

    humanDuration,
    endApproaching,

    uploadVideo,
    videoIsUploaded,
    lastUploadedCaptureData,
    submitVideo,
  }
}

export type {MediaVideoCaptureOptions}
